import { Component, OnInit, OnDestroy, inject } from '@angular/core';
import { AuthSessionService } from '../../../services/auth-session.service';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Router, NavigationEnd } from '@angular/router';
import { Subject } from 'rxjs';
import { catchError, filter, takeUntil } from 'rxjs/operators';
import { throwError } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { ApiUrl } from '../../../shared/models/apiurl.models';
import { LoadingMaskServiceService } from '../../../components/loading-frame/loading-frame.component';
import { LoginAction, LoginService } from '../../../components/session/login/login.service';
import { ProfileStateService } from '../profile-state.service';
import { SiteChromeService } from '../../../services/site-chrome.service';
import { InquiryService } from '../../../services/inquiry.service';

interface UserInfo {
  first_name: string;
  last_name:  string;
  mainPhone:  string;
  avatar:     string;
  is_vip:     boolean;   // ← returned by the backend as is_vip on /user/user_info/
}

@Component({
    selector: 'app-personal-shell',
    templateUrl: './personal-shell.component.html',
    styleUrls: ['./personal-shell.component.scss'],
    standalone: false
})
export class PersonalShellComponent implements OnInit, OnDestroy {
  private readonly authSession = inject(AuthSessionService);

  userInfo: UserInfo = { first_name: '', last_name: '', mainPhone: '', avatar: '', is_vip: false };
  loading    = true;
  totalAds   = 0;       // placeholder — shell doesn't load ads; badge hidden when 0
  currentPath = '';
  /** Mobile: the sidebar opens as a slide-in menu from the right. */
  menuOpen   = false;

  /** Shortcut — true when the backend returns is_vip: true */
  get isVip(): boolean { return this.userInfo.is_vip === true; }

  private destroy$ = new Subject<void>();

  private get httpOptions() {
    return {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'Authorization': this.apiKey.getJWTToken()
      })
    };
  }

  constructor(
    private http: HttpClient,
    private apiKey: ApiUrl,
    private loadingService: LoadingMaskServiceService,
    public translate: TranslateService,
    private router: Router,
    private loginService: LoginService,
    private profileState: ProfileStateService,
    private siteChrome: SiteChromeService,
    public inquiries: InquiryService,
  ) {}

  ngOnInit(): void {
    this.currentPath = this.router.url;
    this.router.events.pipe(
      filter(e => e instanceof NavigationEnd),
      takeUntil(this.destroy$)
    ).subscribe((e: any) => {
      this.currentPath = e.urlAfterRedirects;
      this.menuOpen = false;   // close the mobile menu after navigating
    });
    this.loadUserInfo();
    this.inquiries.refreshUnread();
    this.profileState.updated$
      .pipe(takeUntil(this.destroy$))
      .subscribe(user => {
        this.userInfo = { ...this.userInfo, ...user };
        this.siteChrome.setHidden(this.isVip);
      });
  }

  ngOnDestroy(): void {
    // Leaving the personal area: bring the site header / footer back.
    this.siteChrome.setHidden(false);
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadUserInfo(): void {
    this.http.get<UserInfo>(this.apiKey.getUserInfo(), this.httpOptions)
      .pipe(takeUntil(this.destroy$), catchError(e => throwError(e.error || e)))
      .subscribe({
        next: (res) => {
          this.userInfo = res;
          // isVip comes straight from the API — no ads loading required
          this.loading = false;
          // The VIP shell has its own top bar — hide the site header on
          // every personal page (user-info, my-ads, store…), not only on
          // the dashboard.
          this.siteChrome.setHidden(this.isVip);
        },
        error: () => { this.loading = false; }
      });
  }

  crumbLabel(): string {
    if (this.currentPath.includes('/vip-dashboard'))    return 'לוח בקרה';
    if (this.currentPath.includes('/feed'))             return 'עוקב אחרי';
    if (this.currentPath.includes('/my-ads'))           return 'המודעות שלי';
    if (this.currentPath.includes('/user-info'))        return 'פרופיל';
    if (this.currentPath.includes('/wishlist'))         return 'רשימת משאלות';
    if (this.currentPath.includes('/vip-subscription')) return 'חבילות VIP';
    if (this.currentPath.includes('/benefits'))         return 'ההטבות שלי';
    return 'אזור אישי';
  }

  logout(): void {
    this.menuOpen = false;
    // POD1-7: full logout - revoke the refresh token server-side and drop
    // the socket, not just clear the local flag.
    this.authSession.logout().subscribe();
    this.loginService.sendClickEvent(LoginAction.Close);
    void this.router.navigateByUrl('/');
  }
}
